import React, { useState } from "react";
import ReactModal from "react-modal";
import Select from "react-select";
import NumberFormat from "react-number-format";
import { LoadingButton } from "../shared/loading-button";

const TRANSACTION_TYPES = [
  { label: "To the Ledger", value: "To the Ledger" },
  { label: "From the Ledger", value: "From the Ledger" },
];

const PAYMENT_CATEGORIES_TO_LEDGER = [
  "Account Activation",
  "Refund - Partial Trip",
  "Refund - Canceled Trip",
  "Refund - Overpayment",
  "Funds on File (General)",
  "Membership Credits",
].map((v) => ({ label: v, value: v }));

const PAYMENT_CATEGORIES_FROM_LEDGER = [
  "Flight Charges",
  "Catering",
  "Ground Transportation",
  "Other Incidentals",
  "Membership Canceled",
  "Returned Funds on File",
].map((v) => ({ label: v, value: v }));

const SOURCES = [
  "Funds on File (General)",
  "Funds on File (Membership)",
  "Membership Credits",
].map((v) => ({ label: v, value: v }));

const EMPTY_FORM = {
  transactionType: null,
  amount: "",
  paymentCategory: null,
  source: null,
  notes: "",
};

const TransferToLedgerModal = ({ open, contract, onSubmit, onClose, saving }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const isFromLedger = form.transactionType?.value === "From the Ledger";
  const notesRequired = isFromLedger && form.paymentCategory?.value === "Other Incidentals";
  const categoryOptions = isFromLedger ? PAYMENT_CATEGORIES_FROM_LEDGER : PAYMENT_CATEGORIES_TO_LEDGER;

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    onClose();
  };

  const validate = () => {
    const e = {};
    if (!form.transactionType) e.transactionType = "Required";
    if (!form.amount) e.amount = "Required";
    if (!form.paymentCategory) e.paymentCategory = "Required";
    if (!form.source) e.source = "Required";
    if (notesRequired && !form.notes.trim()) e.notes = "Required for Other Incidentals";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    await onSubmit({
      contractId: contract._id,
      transactionType: form.transactionType.value,
      amount: Math.abs(parseFloat(form.amount)),
      paymentCategory: form.paymentCategory.value,
      source: form.source.value,
      notes: form.notes,
    });

    setForm(EMPTY_FORM);
    setErrors({});
  };

  const handleTransactionTypeChange = (selected) => {
    const newIsFromLedger = selected?.value === "From the Ledger";
    setForm((f) => ({
      ...f,
      transactionType: selected,
      paymentCategory: null,
      amount: f.amount !== "" ? Math.abs(f.amount) * (newIsFromLedger ? 1 : -1) : "",
    }));
  };

  return (
    <ReactModal
      isOpen={open}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={handleClose}
      style={{
        overlay: {
          zIndex: 999999,
          backgroundColor: "rgb(32 53 68 / 50%)",
        },
        content: {
          width: "auto",
          border: "none",
          overflowY: "scroll",
          maxHeight: "100vh",
          minWidth: "35vw",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header base-modal">
        <h4>Transfer to/from Funds Ledger</h4>
      </div>

      <div className="modal-body base-modal overflow-scroll">
        <form className="w-100 base-form mt-20" onSubmit={handleSubmit}>
          {contract && (
            <p className="mb-3 text-muted">
              Contract: <strong>{contract.contractTag}</strong>
            </p>
          )}

          <div className="form-group mb-3">
            <label className="base-label">Transaction Type <span className="text-danger">*</span></label>
            <Select
              options={TRANSACTION_TYPES}
              value={form.transactionType}
              onChange={handleTransactionTypeChange}
              placeholder="Select..."
              classNamePrefix="select"
            />
            {errors.transactionType && <small className="text-danger">{errors.transactionType}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Amount <span className="text-danger">*</span></label>
            <NumberFormat
              value={form.amount}
              thousandSeparator
              prefix={"$"}
              placeholder="Amount"
              className={`semibold-text amount-${form.amount >= 0 ? "positive" : "negative"}`}
              decimalScale={2}
              fixedDecimalScale
              allowNegative={!isFromLedger}
              onValueChange={({ floatValue }) =>
                setForm((f) => {
                  if (floatValue == null) return { ...f, amount: "" };
                  const sign = f.transactionType?.value === "From the Ledger" ? 1 : -1;
                  return { ...f, amount: Math.abs(floatValue) * sign };
                })
              }
            />
            {errors.amount && <small className="text-danger">{errors.amount}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Payment Category <span className="text-danger">*</span></label>
            <Select
              options={categoryOptions}
              value={form.paymentCategory}
              onChange={(v) => setForm((f) => ({ ...f, paymentCategory: v }))}
              placeholder="Select..."
              isDisabled={!form.transactionType}
              classNamePrefix="select"
            />
            {errors.paymentCategory && <small className="text-danger">{errors.paymentCategory}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Source <span className="text-danger">*</span></label>
            <Select
              options={SOURCES}
              value={form.source}
              onChange={(v) => setForm((f) => ({ ...f, source: v }))}
              placeholder="Select..."
              classNamePrefix="select"
            />
            {errors.source && <small className="text-danger">{errors.source}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Notes {notesRequired && <span className="text-danger">*</span>}</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
            {errors.notes && <small className="text-danger">{errors.notes}</small>}
          </div>
        </form>
      </div>

      <div className="modal-footer no-border w-100 d-flex gap-10">
        <button className="base-button btn-danger flex-half" disabled={saving} onClick={handleClose} type="button">
          Cancel
        </button>

        <LoadingButton
          className="base-button evo-blue flex-half"
          onClick={handleSubmit}
          isLoading={saving}
          type="button"
        >
          Submit
        </LoadingButton>
      </div>
    </ReactModal>
  );
};

export default TransferToLedgerModal;
